import { reactive } from 'vue'

/**
 * Toast manager — Vue port of `lxToast()` from the static mockup.
 * State is a module-level reactive list so any component can read it,
 * and any component can push a toast via the returned helper.
 */
export interface Toast {
  id: number
  title: string
  sub: string
  type: string
  actionLabel: string | null
  onAction: (() => void) | null
}

export interface ToastOptions extends Partial<Omit<Toast, 'id'>> {
  /** Auto-dismiss after this many ms; 0 keeps it until dismissed. */
  duration?: number
}

const state = reactive<{ toasts: Toast[] }>({ toasts: [] })

let _seq = 0

function show({
  title = '',
  sub = '',
  type = 'success',
  duration = 4000,
  actionLabel = null,
  onAction = null,
}: ToastOptions = {}): number {
  const id = ++_seq
  const toast = { id, title, sub, type, actionLabel, onAction }
  state.toasts.push(toast)
  if (duration > 0) {
    setTimeout(() => dismiss(id), duration)
  }
  return id
}

function dismiss(id: number): void {
  const idx = state.toasts.findIndex((t) => t.id === id)
  if (idx >= 0) state.toasts.splice(idx, 1)
}

export function useToast() {
  return {
    toasts: state.toasts,
    toast: show,
    dismiss,
  }
}
